import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from, of, switchMap, tap } from 'rxjs';
import { SavedCopy, openSavedCopy } from '../utils/patient-list-saved-copy';

/** Bump when a kept list's shape changes, so older copies are dropped. */
const SETTINGS_VERSION = '1';

export interface SavedSettings {
  lists: Record<string, { stamp: string; data: any }>;
}

interface Visit {
  groupId: string;
  stamps: Record<string, string> | null;
  saved: SavedSettings;
  copy: SavedCopy<SavedSettings> | null;
}

/**
 * The practice's settings lists (appointment types, note types, coding bundles and the rest) kept on the device, so a
 * practice switch does not load them all again. On each visit to a practice one request asks the server for a stamp
 * per list (`GET /groups/:groupId/settings-stamps`: last change and row count), and a list is loaded again only when
 * its stamp changed. Lists are encrypted on the device with the patient list's saved copy, under the
 * `practice-settings` key, and kept in memory for the session.
 *
 * Only the load on a practice switch reads through here. A reload after an edit goes to the server; the edit
 * changes the stamp, so the kept copy is replaced on the next visit.
 */
@Injectable({
  providedIn: 'root'
})
export class PracticeSettingsStore {
  private apiUrl = 'api';
  private visit: Promise<Visit> | null = null;
  private visitGroupId: string | null = null;
  private saved = new Map<string, SavedSettings>();
  private copies = new Map<string, Promise<SavedCopy<SavedSettings> | null>>();
  private pending: Promise<void> | null = null;

  /** Replaced in specs. */
  openCopy: (key: string, version: string, groupId: string) => Promise<SavedCopy<SavedSettings>> = openSavedCopy;

  constructor(private http: HttpClient) { }

  /**
   * The list for this practice: the kept copy while its stamp is unchanged, otherwise `load()`, which is then kept.
   * `list` may carry a qualifier after a colon (`consentTemplates:Orthopaedics`); the stamp is the part before it.
   */
  read<T>(groupId: string, list: string, load: () => Observable<T>): Observable<T> {
    return from(this.visitTo(groupId)).pipe(
      switchMap(visit => {
        const stamp = visit.stamps?.[list.split(':')[0]];
        const kept = visit.saved.lists[list];
        // Copies both ways: services change their lists in place (the hospitals' wards, sorting).
        if (stamp && kept?.stamp === stamp) return of(structuredClone(kept.data) as T);
        return load().pipe(tap(data => {
          if (!stamp) return;
          visit.saved.lists[list] = { stamp, data: structuredClone(data) };
          this.persist(visit);
        }));
      })
    );
  }

  /** Waits for the device copy to be written. */
  async flush(): Promise<void> {
    await this.pending;
  }

  /** A new visit, with fresh stamps, whenever the practice differs from the last one read. */
  private visitTo(groupId: string): Promise<Visit> {
    if (!this.visit || this.visitGroupId !== groupId) {
      this.visitGroupId = groupId;
      this.visit = this.startVisit(groupId);
    }
    return this.visit;
  }

  private async startVisit(groupId: string): Promise<Visit> {
    const [stamps, copy] = await Promise.all([this.stampsOf(groupId), this.copyOf(groupId)]);
    let saved = this.saved.get(groupId);
    if (!saved) {
      saved = (copy && await copy.get().catch(() => undefined)) || { lists: {} };
      this.saved.set(groupId, saved);
    }
    return { groupId, stamps, saved, copy };
  }

  private async stampsOf(groupId: string): Promise<Record<string, string> | null> {
    try {
      const res: any = await firstValueFrom(this.http.get(`${this.apiUrl}/groups/${groupId}/settings-stamps`, this.authHeaders()));
      return res?.stamps || null;
    } catch {
      return null;
    }
  }

  /** One device copy per practice for the session; none where the key, IndexedDB or WebCrypto is unavailable. */
  private copyOf(groupId: string): Promise<SavedCopy<SavedSettings> | null> {
    if (!this.copies.has(groupId)) {
      this.copies.set(groupId, (async () => {
        try {
          const res: any = await firstValueFrom(this.http.get(
            `${this.apiUrl}/patients/group/${groupId}/cache-key?purpose=practice-settings`, this.authHeaders()));
          return await this.openCopy(res.key, `${SETTINGS_VERSION}.${res.version}`, groupId);
        } catch {
          return null;
        }
      })());
    }
    return this.copies.get(groupId)!;
  }

  /** Lists arrive together on a switch; they are written to the device once they have. */
  private persist(visit: Visit): void {
    if (!visit.copy) return;
    const copy = visit.copy;
    this.pending = (this.pending || Promise.resolve())
      .then(() => new Promise<void>(resolve => setTimeout(resolve)))
      .then(() => copy.put(visit.saved))
      .catch(() => undefined);
  }

  private authHeaders() {
    return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  }
}
